import { DecoratorNode, $applyNodeReplacement } from 'lexical';
import * as React from 'react';
import PageLinkComponent from './PageLinkComponent';

/**
 * A block-level link to another document in the same workspace — the chip the
 * "Subpage" slash command inserts (and, later, any "link to page" affordance).
 *
 * Identity is `{ docId, scopeType, teamId }`; `title` is a CREATION-TIME CACHE
 * of the target's name so a host without a PageLinkContext provider (or a
 * document opened before its tree loads) still shows something meaningful.
 * The live title always comes from the provider's `resolve(docId)` — see
 * PageLinkComponent.
 *
 * Saved-HTML shape (what exportDOM writes and importDOM claims):
 *   <a data-doc-link="<docId>" data-doc-scope="personal|team:<id>">Title</a>
 * importDOM claims 'a' ONLY when data-doc-link is present, at a priority above
 * LinkNode's (1) — plain anchors stay LinkNode's. sanitizeHtml.js allowlists
 * exactly these two attributes (validated) on 'a'.
 */

const SCOPE_TEAM_RE = /^team:(\d+)$/;

/** node fields -> the serialized data-doc-scope attribute value. */
export function pageLinkScopeAttr(scopeType, teamId) {
  if (scopeType === 'team' && teamId != null) return `team:${teamId}`;
  // Free plan, cloud/local items: a page kept on this device.
  return scopeType === 'device' ? 'device' : 'personal';
}

/** data-doc-scope attribute value -> { scopeType, teamId } node fields. */
export function parsePageLinkScopeAttr(value) {
  const m = SCOPE_TEAM_RE.exec(value || '');
  if (m) return { scopeType: 'team', teamId: Number(m[1]) };
  if (value === 'device') return { scopeType: 'device', teamId: null };
  return { scopeType: 'personal', teamId: null };
}

function $convertPageLinkElement(domNode) {
  const docId = domNode.getAttribute('data-doc-link') || '';
  if (!docId) return null;
  const { scopeType, teamId } = parsePageLinkScopeAttr(domNode.getAttribute('data-doc-scope'));
  const node = $createPageLinkNode({
    docId,
    scopeType,
    teamId,
    title: domNode.textContent || '',
  });
  // The anchor's text child is the cached title, already captured above —
  // $createNodesFromDOM discards children of a decorator conversion, so
  // nothing else is needed to keep it from leaking as a sibling text node
  // (pinned by the round-trip test).
  return { node };
}

export class PageLinkNode extends DecoratorNode {
  __docId;
  __scopeType; // 'personal' | 'device' | 'team'
  __teamId; // number | null
  __title; // creation-time cache, see module doc

  static getType() {
    return 'page-link';
  }

  static clone(node) {
    return new PageLinkNode(node.__docId, node.__scopeType, node.__teamId, node.__title, node.__key);
  }

  constructor(docId, scopeType, teamId, title, key) {
    super(key);
    this.__docId = docId;
    this.__scopeType = scopeType === 'team' ? 'team' : scopeType === 'device' ? 'device' : 'personal';
    this.__teamId = this.__scopeType === 'team' && teamId != null ? teamId : null;
    this.__title = title || '';
  }

  static importJSON(serializedNode) {
    return $createPageLinkNode({
      docId: serializedNode.docId,
      scopeType: serializedNode.scopeType,
      teamId: serializedNode.teamId ?? null,
      title: serializedNode.title,
    });
  }

  exportJSON() {
    return {
      ...super.exportJSON(),
      type: 'page-link',
      version: 1,
      docId: this.__docId,
      scopeType: this.__scopeType,
      teamId: this.__teamId,
      title: this.__title,
    };
  }

  static importDOM() {
    return {
      a: (domNode) => {
        // Claim ONLY page-link anchors; every plain <a> must keep falling
        // through to LinkNode's own converter (priority 1).
        if (!domNode.getAttribute || !domNode.getAttribute('data-doc-link')) return null;
        return { conversion: $convertPageLinkElement, priority: 2 };
      },
    };
  }

  exportDOM() {
    const element = document.createElement('a');
    element.setAttribute('data-doc-link', this.__docId);
    element.setAttribute('data-doc-scope', pageLinkScopeAttr(this.__scopeType, this.__teamId));
    element.textContent = this.__title || 'Untitled';
    return { element };
  }

  // A block chip, not an inline token: inserting it from the slash menu splits
  // the paragraph and the node lands at root level (a saved-HTML reimport may
  // wrap it back in a <p>; the chip renders full-width either way).
  isInline() {
    return false;
  }

  createDOM(config) {
    const span = document.createElement('span');
    const className = config.theme.pageLink;
    if (className) span.className = className;
    return span;
  }

  updateDOM() {
    return false;
  }

  getTextContent() {
    return this.__title || '';
  }

  getDocId() {
    return this.getLatest().__docId;
  }

  getScopeType() {
    return this.getLatest().__scopeType;
  }

  getTeamId() {
    return this.getLatest().__teamId;
  }

  getTitle() {
    return this.getLatest().__title;
  }

  decorate() {
    return (
      <PageLinkComponent
        nodeKey={this.__key}
        docId={this.__docId}
        scopeType={this.__scopeType}
        teamId={this.__teamId}
        title={this.__title}
      />
    );
  }
}

export function $createPageLinkNode({ docId, scopeType = 'personal', teamId = null, title = '' } = {}) {
  return $applyNodeReplacement(new PageLinkNode(docId, scopeType, teamId, title));
}

export function $isPageLinkNode(node) {
  return node instanceof PageLinkNode;
}
